import React from "react"
import { Badge } from "~/components/reusable/Badge"
import { Tooltip } from "~/components/reusable/Tooltip"
import {
  FORECAST_QUALITY_DESCRIPTIONS,
  getForecastQualityLevel,
  FORECAST_QUALITY_THRESHOLDS,
} from "~/constants/forecastEvaluation"
import { formatNumber } from "~/utils/formatting"
import MiniTable from "~/components/reusable/miniTable/MiniTable"
import { Check, ExclamationMark } from "@phosphor-icons/react"
import LeadTimePerformanceSection from "./LeadTimePerformanceSection"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"

const getQualityIconForValue = (
  value: number,
  lowToModerateThreshold: number = FORECAST_QUALITY_THRESHOLDS.LOW_TO_MODERATE,
  moderateToHighThreshold: number = FORECAST_QUALITY_THRESHOLDS.MODERATE_TO_HIGH,
  size: number = 14,
) => {
  if (value >= moderateToHighThreshold) {
    return <Check className="text-badge-green-fg-text" size={size} />
  } else if (value >= lowToModerateThreshold) {
    return <ExclamationMark className="text-badge-orange-fg-text" size={size} />
  } else {
    return <ExclamationMark className="text-badge-red-fg-text" size={size} />
  }
}

interface ForecastQualityBadgeProps {
  quality: number | null | undefined
  oneMonthScore?: number | null
  threeMonthsScore?: number | null
  sixMonthsScore?: number | null
  twelveMonthsScore?: number | null
  showNumbers?: boolean
  showTooltip?: boolean
  /** When provided, the tooltip also shows lead-time forecast performance for these variants. */
  variantIds?: string[]
  /** When set (SKU-level row), the lead-time panel is scoped to this SKU instead of the variant. */
  skuId?: string
  /**
   * PO/buy contexts only: the warehouses the PO orders into. Scopes the lead-time forecast & sales
   * to those warehouses AND gates the whole lead-time panel — omitted/empty => no lead-time panel
   * (non-PO tables show quality only).
   */
  warehouseIds?: string[]
}

const ForecastQualityBadge: React.FC<ForecastQualityBadgeProps> = ({
  quality,
  oneMonthScore,
  threeMonthsScore,
  sixMonthsScore,
  twelveMonthsScore,
  showNumbers = false,
  showTooltip = true,
  variantIds,
  skuId,
  warehouseIds,
}) => {
  if (quality === null || quality === undefined) {
    return (
      <Badge variant="gray" size="sm">
        N/A
      </Badge>
    )
  }

  const qualityLevel = getForecastQualityLevel(quality)
  const variant = qualityLevel as "red" | "yellow" | "green"

  const tooltipText = FORECAST_QUALITY_DESCRIPTIONS[qualityLevel]

  const oneMonth =
    oneMonthScore !== undefined && oneMonthScore !== null
      ? formatNumber(oneMonthScore * 100, 0)
      : "-"
  const threeMonths =
    threeMonthsScore !== undefined && threeMonthsScore !== null
      ? formatNumber(threeMonthsScore * 100, 0)
      : "-"
  const sixMonths =
    sixMonthsScore !== undefined && sixMonthsScore !== null
      ? formatNumber(sixMonthsScore * 100, 0)
      : "-"
  const twelveMonths =
    twelveMonthsScore !== undefined && twelveMonthsScore !== null
      ? formatNumber(twelveMonthsScore * 100, 0)
      : "-"

  const getScoreBadge = (score: string) => {
    if (score === "-") {
      return (
        <div className="flex justify-center bg-subtle text-fg-subtle">
          <Badge variant="gray" size="xs" className="mt-[1px]">
            <p className="text-xs">N/A</p>
          </Badge>
        </div>
      )
    }

    const cleanScore = score.replace(/\./g, "").replace(",", ".")
    const numericScore = parseFloat(cleanScore)
    const qualityLevel = getForecastQualityLevel(numericScore)
    const variant = qualityLevel as "red" | "yellow" | "green"

    return (
      <div className="flex items-center justify-center bg-subtle text-fg-subtle">
        <Badge variant={variant} size="xs" className="mt-[1px]">
          {showNumbers ? score : getQualityIconForValue(numericScore)}
        </Badge>
      </div>
    )
  }

  const qualityBody = (
    <>
      <span className="label-base">Forecast quality</span>
      <p className="mb-2 paragraph-base">{tooltipText}</p>
      <MiniTable
        headers={["1 month", "3 months", "6 months", "12 months"]}
        rows={[
          [
            getScoreBadge(oneMonth),
            getScoreBadge(threeMonths),
            getScoreBadge(sixMonths),
            getScoreBadge(twelveMonths),
          ],
        ]}
      />
    </>
  )

  const body = <div className="rounded-[12px] p-3 text-left">{qualityBody}</div>

  const getQualityIcon = () => {
    return getQualityIconForValue(quality)
  }

  // This code is only reached when quality is not null or undefined
  const badgeContent = (
    <Badge variant={variant} size="xs">
      {showNumbers ? formatNumber(Number(quality), 0) : getQualityIcon()}
    </Badge>
  )

  if (!showTooltip) return badgeContent

  // HoverCard instead of Tooltip because the panel is interactive — Radix tooltips close when the
  // pointer enters their content. Only PO/buy contexts pass warehouseIds, which gates the panel.
  const showLeadTimePanel =
    warehouseIds !== undefined &&
    warehouseIds.length > 0 &&
    (skuId !== undefined || (variantIds !== undefined && variantIds.length > 0))
  if (showLeadTimePanel) {
    return (
      <HoverCard openDelay={100} closeDelay={100}>
        <HoverCardTrigger asChild>{badgeContent}</HoverCardTrigger>
        {/* Cap to the viewport-available height and scroll inside — the lead-time tables can make
            this panel taller than a small screen. */}
        <HoverCardContent
          align="start"
          collisionPadding={8}
          className="max-h-[var(--radix-hover-card-content-available-height)] w-auto max-w-[450px] overflow-x-hidden overflow-y-auto p-3 text-left"
        >
          {qualityBody}
          <LeadTimePerformanceSection
            variantIds={variantIds ?? []}
            skuId={skuId}
            warehouseIds={warehouseIds}
          />
        </HoverCardContent>
      </HoverCard>
    )
  }

  return (
    <Tooltip className="max-w-[450px]" body={body}>
      {badgeContent}
    </Tooltip>
  )
}

export default ForecastQualityBadge
